import { Injectable } from '@angular/core';
import { SupabaseService } from '../../services/supabase.service';
import { TablesInsert } from '../database.types';
import { dateFormat, TimeFrame } from '../../tools/date';
import dayjs from 'dayjs';
import { EntryRepository } from './entry.repository';
import { firstValueFrom } from 'rxjs';
import { TopicRepository, topicsObj$ } from '../topic/topic.repository';
import { EntryWithTopic } from '../entry-with-topic';
import { SupaStatisticsApi } from '../supa-statistics/supa-statistics.api';

const fullSelect = `
        id,
        date,
        value,
        content,
        created_at,
        topic_id
`;

export interface GetBlogEntriesParams {
  topicIds: number[];
  searchTerm: string;
  startDate: string | null;
  endDate: string | null;
  month: string | null;
}

@Injectable({ providedIn: 'root' })
export class EntryApi {
  constructor(
    private supabase: SupabaseService,
    private topicRepository: TopicRepository,
    private entryRepository: EntryRepository,
    private statisticsApi: SupaStatisticsApi,
  ) {}

  async fetchEntriesByDate(date: string) {
    const { data, error } = await this.supabase.client
      .from('entries')
      .select(fullSelect)
      .eq('date', date);

    if (!data) {
      throw Error();
    }

    this.entryRepository.upsertEntries(data);
  }

  async fetchEntriesByTimeFrame(timeFrame: TimeFrame) {
    const { data, error } = await this.supabase.client
      .from('entries')
      .select(fullSelect)
      .gte('date', dayjs(timeFrame.startDate).format(dateFormat))
      .lte('date', dayjs(timeFrame.endDate).format(dateFormat));

    if (!data) {
      throw Error();
    }

    this.entryRepository.upsertEntries(data);
  }

  async getBlogEntries(
    params: Partial<GetBlogEntriesParams>,
  ): Promise<EntryWithTopic[]> {
    const { topicIds, searchTerm, startDate, endDate, month } = params;

    let request = this.supabase.client.from('entries').select(fullSelect);

    if (!!topicIds && topicIds.length !== 0) {
      request.in('topic_id', topicIds);
    }

    if (!!startDate) {
      request.gte('date', startDate);
    }

    if (!!endDate) {
      request.lte('date', endDate);
    }

    if (!!month) {
      request.gte('date', dayjs(month).startOf('month'));
      request.lte('date', dayjs(month).endOf('month'));
    }

    if (!!searchTerm) {
      request.textSearch('content', searchTerm);
    }

    const { data, error } = await request
      .order('date', { ascending: false })
      .range(0, 25);

    if (!data) {
      throw Error();
    }

    const topicsObj = await firstValueFrom(topicsObj$);
    return data.map((entry) => ({
      ...entry,
      topic: topicsObj[entry.topic_id],
    }));
  }

  async upsert(entry: TablesInsert<'entries'>) {
    if (!entry.content && !entry.value) {
      if (entry.id) {
        await this.removeEntry(entry.id);
      }
      return;
    }

    const { data, error } = await this.supabase.client
      .from('entries')
      .upsert(entry)
      .select(fullSelect)
      .single();

    if (!data) {
      throw Error();
    }

    if (!entry.id) {
      this.statisticsApi.fetchStreaks();
      this.statisticsApi.fetchMonthlyStreaks();
      this.topicRepository.removeTemp(entry.topic_id);
    }
    this.entryRepository.upsertEntries([data]);
  }

  async removeEntry(id: number) {
    const { error } = await this.supabase.client
      .from('entries')
      .delete()
      .eq('id', id);

    if (!error) {
      this.entryRepository.deleteEntries([id]);
    }
  }

  async insert(importEntries: TablesInsert<'entries'>[]) {
    const { data, error } = await this.supabase.client
      .from('entries')
      .insert(importEntries)
      .select('id');

    if (!data) {
      throw Error();
    }
    console.log('import successful', data.length);
  }
}
